import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

// Lazy load components for performance
const TranscriptionsList = React.lazy(() => import("./TranscriptionsList"));
const NewTranscription = React.lazy(() => import("./NewTranscription"));
const TranscriptionDetails = React.lazy(() => import("./TranscriptionDetails"));
const TranscriptionsWhatsNew = React.lazy(() => import("./TranscriptionsWhatsNew"));

/**
 * TRANSCRIPTION SERVICE MODULE CONFIGURATION
 *
 * Audio/Video transcription service using LemonFox AI.
 * Files are uploaded to the own IPFS server and sent to LemonFox for transcription.
 *
 * PERMISSIONS:
 * - TRANSCRIPTION_USER: Can create and view own transcriptions
 * - TRANSCRIPTION_ADMIN: Can view/manage all transcriptions in tenant, test API connection
 *
 * ROUTES:
 * - /transcriptions - List all transcriptions (filtered by permission)
 * - /transcriptions/new - Create new transcription
 * - /transcriptions/:id - View transcription details and download
 */
const transcriptionsModuleConfig: ModuleConfig = {
  // MODULE IDENTITY
  id: "transcriptions",
  name: "Transcriptions",
  description: "AI-powered audio and video transcription service using LemonFox",
  version: "1.1.0",
  enabled: true,
  category: "productivity",
  order: 45,
  icon: "🎤",
  color: "#9F7AEA",

  // ROUTES
  routes: [
    {
      path: "/transcriptions",
      component: TranscriptionsList,
      permissionLevel: "view",
      useLayout: true
    },
    {
      path: "/transcriptions/new",
      component: NewTranscription,
      permissionLevel: "create",
      useLayout: true
    },
    {
      path: "/transcriptions/whats-new",
      component: TranscriptionsWhatsNew,
      permissionLevel: "view",
      useLayout: true
    },
    {
      path: "/transcriptions/:id",
      component: TranscriptionDetails,
      permissionLevel: "view",
      useLayout: true
    }
  ],

  // NAVIGATION
  navigation: [
    {
      label: "All Transcriptions",
      path: "/transcriptions",
      icon: "📋",
      permissionLevel: "view"
    },
    {
      label: "New Transcription",
      path: "/transcriptions/new",
      icon: "➕",
      permissionLevel: "create"
    },
    {
      label: "What's New",
      path: "/transcriptions/whats-new",
      icon: "✨",
      permissionLevel: "view"
    }
  ],

  // PERMISSIONS (resolved from backend single-source-of-truth)
  permissions: {
    view: [],
    create: [],
    edit: [],
    delete: []
  },

  // QUICK ACTIONS
  quickActions: [
    {
      label: "New Transcription",
      path: "/transcriptions/new",
      icon: "🎤",
      description: "Upload and transcribe audio/video files"
    }
  ],

    // 📝 CHANGELOG
    changelog: [
        {
            version: "1.1.0",
            releaseDate: "2025-11-27",
            title: "Enhanced Upload Experience",
            features: [
                "✨ Drag and drop file upload with auto-start",
                "✨ Multi-phase progress tracking (upload + transcription)",
                "✨ Real-time ETA and speed calculations",
                "✨ Audio/video preview before upload",
                "✨ File size validation (500MB limit)",
                "✨ Audio player on transcription details page",
                "✨ Download original audio file button",
                "✨ Improved retry functionality",
                "🐛 Fixed table overflow on transcriptions list",
                "🐛 Fixed long file name overflow on details page"
            ],
            notes: "Major UX improvements with drag-and-drop, preview, and better progress tracking."
        },
        {
            version: "1.0.0",
            releaseDate: "2025-11-22",
            title: "Stable Release - Production Ready",
            features: [
                "✅ Production-ready AI transcription service",
                "✅ LemonFox AI integration for accurate transcriptions",
                "✅ IPFS file storage (self-hosted)",
                "✅ Audio and video file support",
                "✅ Transcription download and export",
                "✅ Role-based access control (TRANSCRIPTION_USER, TRANSCRIPTION_ADMIN)"
            ],
            notes: "Official stable release with enterprise-grade transcription capabilities."
        },
        {
            version: "0.2.0",
            releaseDate: "2025-10-31",
            title: "IPFS Server Integration",
            features: [
                "✨ Added own IPFS server support (3 lines)",
                "✨ Enhanced file storage reliability",
                "✨ Better control over media hosting",
                "✨ Improved download functionality"
            ],
            notes: "Self-hosted IPFS integration for transcription files."
        },
        {
            version: "0.1.0",
            releaseDate: "2025-10-02",
            title: "Initial Transcription Service",
            features: [
                "✅ Created TranscriptionsList component (291 lines)",
                "✅ Created NewTranscription component (388 lines)",
                "✅ Created TranscriptionDetails component (555 lines)",
                "✅ Module configuration and routing (90 lines)",
                "✅ Created implementation guide (199 lines)",
                "✅ LemonFox AI API integration",
                "✅ Self-hosted IPFS upload integration",
                "✅ Audio/video file processing",
                "✅ Transcription status tracking"
            ],
            notes: "Initial implementation with 1,523 lines of AI transcription code."
        }
    ],

    // 🗺️ ROADMAP
    roadmap: [
        {
            title: "Speaker Diarization Display",
            description: "Show speaker labels and timestamps in transcription view",
            priority: "high" as const,
            status: "planned" as const,
            targetVersion: "1.2.0",
            features: [
                "Display speaker labels (SPEAKER_00, SPEAKER_01, etc.)",
                "Color-coded speaker segments",
                "Timestamp markers for each speaker turn",
                "Jump to speaker segment on click"
            ]
        },
        {
            title: "Export Formats",
            description: "Export transcriptions to multiple formats",
            priority: "high" as const,
            status: "planned" as const,
            targetVersion: "1.2.0",
            features: [
                "Export to SRT subtitle format",
                "Export to VTT subtitle format",
                "Export to Word document (.docx)",
                "Export to PDF with formatting"
            ]
        },
        {
            title: "Batch Transcription",
            description: "Upload and transcribe multiple files at once",
            priority: "medium" as const,
            status: "planned" as const,
            targetVersion: "1.3.0",
            features: [
                "Multi-file upload queue",
                "Batch progress tracking",
                "Parallel processing for faster completion",
                "Bulk download of completed transcriptions"
            ]
        },
        {
            title: "Transcription Editing",
            description: "Edit and correct transcription text inline",
            priority: "medium" as const,
            status: "planned" as const,
            targetVersion: "1.3.0",
            features: [
                "Inline text editing",
                "Speaker label correction",
                "Timestamp adjustment",
                "Version history of edits"
            ]
        },
        {
            title: "AI Summary Generation",
            description: "Generate AI-powered summaries and key points from transcriptions",
            priority: "low" as const,
            status: "planned" as const,
            targetVersion: "2.0.0",
            features: [
                "Automatic key points extraction",
                "Meeting summary generation",
                "Action items detection",
                "Topic segmentation"
            ]
        }
    ]

};

export default transcriptionsModuleConfig;
